import type { AuthService } from "@/generated/public-contracts";
import type { QuranReflectPostsFacade } from "@/runtime/quran-reflect-posts";
import type {
  ApiParams,
  ChapterId,
  ContentSyncOptions,
  ContentSyncResourceGroup,
  HizbNumber,
  HTTPMethod,
  JuzNumber,
  OperationRequest,
  PageNumber,
  PublicClientConfig,
  RubNumber,
  SearchParams,
  ServerClientConfig,
  TokenResponse,
  VerseKey,
} from "@/types";
import { operationCatalog } from "@/generated/contracts";
import { toUserSession } from "@/lib/http-utils";
import { createGeneratedGroups, createRawClient } from "@/lib/runtime-utils";
import { replacePathParams } from "@/lib/url";
import { createAnalyticsFacade } from "@/runtime/analytics";
import { createAppStateFacade } from "@/runtime/app-state";
import { createQuranReflectPostsFacade } from "@/runtime/quran-reflect-posts";
import { QuranAnswers } from "@/sdk/answers";
import { QuranAudio } from "@/sdk/audio";
import { QuranChapters } from "@/sdk/chapters";
import { QuranFetcher } from "@/sdk/fetcher";
import { QuranHadithReferences } from "@/sdk/hadith-references";
import { QuranJuzs } from "@/sdk/juzs";
import { QuranResources } from "@/sdk/resources";
import { QuranSearch } from "@/sdk/search";
import { QuranVerses } from "@/sdk/verses";

type RuntimeClientConfig = PublicClientConfig | ServerClientConfig;
type RuntimeMode = "public" | "server";
type RawOperation = (request?: OperationRequest) => Promise<unknown>;
type GeneratedGroup = Record<string, RawOperation>;
type QuranReflectFacade = {
  comments: GeneratedGroup;
  posts: QuranReflectPostsFacade;
  raw: Record<string, RawOperation>;
  rooms: GeneratedGroup;
  tags: GeneratedGroup;
  users: GeneratedGroup;
};

const LEGACY_BOOKMARK_GET_MESSAGE =
  "auth.bookmarks.get(bookmarkId) is not supported because /v1/bookmarks/bookmark expects bookmark detail query parameters. Pass a query object such as { mushafId, key, type, verseNumber } or { mushafId, isReading: true }.";

const createUserServiceRequest =
  (fetcher: QuranFetcher, service: AuthService) =>
  (method: HTTPMethod, path: string, request?: OperationRequest) => {
    return fetcher.request<unknown>(
      service,
      replacePathParams(path, request?.path),
      request?.query,
      {
        ...request,
        auth: "user",
        method,
        path: undefined,
        query: undefined,
      },
    );
  };

const createAuthFacade = (fetcher: QuranFetcher) => {
  const raw = createRawClient(fetcher, operationCatalog.auth.v1);
  const generatedGroups = createGeneratedGroups(operationCatalog.auth.v1, raw);
  const request = createUserServiceRequest(fetcher, "auth");

  return {
    ...generatedGroups,
    appState: createAppStateFacade(fetcher),
    collections: {
      ...generatedGroups.collections,
      addBookmark: (collectionId: string, body: Record<string, unknown>) =>
        request("POST", "/v1/collections/{collectionId}/bookmarks", {
          body,
          path: { collectionId },
        }),
      create: (body: Record<string, unknown>) =>
        request("POST", "/v1/collections", { body }),
      get: (collectionId: string, query?: ApiParams) =>
        request("GET", "/v1/collections/{collectionId}", {
          path: { collectionId },
          query,
        }),
      list: (query?: ApiParams) => request("GET", "/v1/collections", { query }),
      listAll: (query?: ApiParams) =>
        request("GET", "/v1/collections/all", { query }),
      remove: (collectionId: string) =>
        request("DELETE", "/v1/collections/{collectionId}", {
          path: { collectionId },
        }),
      removeBookmark: (collectionId: string, bookmarkId: string) =>
        request(
          "DELETE",
          "/v1/collections/{collectionId}/bookmarks/{bookmarkId}",
          {
            path: { bookmarkId, collectionId },
          },
        ),
      update: (collectionId: string, body: Record<string, unknown>) =>
        request("POST", "/v1/collections/{collectionId}", {
          body,
          path: { collectionId },
        }),
    },
    bookmarks: {
      ...generatedGroups.bookmarks,
      create: (body: Record<string, unknown>) =>
        request("POST", "/v1/bookmarks", { body }),
      get: async (query?: ApiParams | string) => {
        if (typeof query === "string") {
          throw new Error(LEGACY_BOOKMARK_GET_MESSAGE);
        }

        return request("GET", "/v1/bookmarks/bookmark", {
          query,
        });
      },
      list: (query?: ApiParams) => request("GET", "/v1/bookmarks", { query }),
      listCollections: (query?: ApiParams) =>
        request("GET", "/v1/bookmarks/collections", { query }),
      listRange: (query?: ApiParams) =>
        request("GET", "/v1/bookmarks/ayahs-range", { query }),
      remove: (bookmarkId: string) =>
        request("DELETE", "/v1/bookmarks/{bookmarkId}", {
          path: { bookmarkId },
        }),
    },
    goals: {
      ...generatedGroups.goals,
      create: (body: Record<string, unknown>) =>
        request("POST", "/v1/goals", { body }),
      estimate: (query?: ApiParams) =>
        request("GET", "/v1/goals/estimate", { query }),
      getTodaysPlan: (query?: ApiParams) =>
        request("GET", "/v1/goals/get-todays-plan", { query }),
      remove: (id: string) =>
        request("DELETE", "/v1/goals/{id}", { path: { id } }),
      update: (id: string, body: Record<string, unknown>) =>
        request("PUT", "/v1/goals/{id}", {
          body,
          path: { id },
        }),
    },
    notes: {
      ...generatedGroups.notes,
      create: (body: Record<string, unknown>) =>
        request("POST", "/v1/notes", { body }),
      get: (noteId: string) =>
        request("GET", "/v1/notes/{noteId}", { path: { noteId } }),
      list: (query?: ApiParams) => request("GET", "/v1/notes", { query }),
      listByAttachedEntity: (query?: ApiParams) =>
        request("GET", "/v1/notes/by-attached-entity", { query }),
      listByRange: (query?: ApiParams) =>
        request("GET", "/v1/notes/by-range", { query }),
      listByVerseKey: (verseKey: string, query?: ApiParams) =>
        request("GET", "/v1/notes/by-verse/{verseKey}", {
          path: { verseKey },
          query,
        }),
      publish: (noteId: string, body?: Record<string, unknown>) =>
        request("POST", "/v1/notes/{noteId}/publish", {
          body,
          path: { noteId },
        }),
      remove: (noteId: string) =>
        request("DELETE", "/v1/notes/{noteId}", { path: { noteId } }),
      update: (noteId: string, body: Record<string, unknown>) =>
        request("PATCH", "/v1/notes/{noteId}", {
          body,
          path: { noteId },
        }),
    },
    preferences: {
      ...generatedGroups.preferences,
      bulkUpdate: (body: Record<string, unknown>) =>
        request("POST", "/v1/preferences/bulk", { body }),
      get: (query?: ApiParams) => request("GET", "/v1/preferences", { query }),
      update: (body: Record<string, unknown>) =>
        request("POST", "/v1/preferences", { body }),
    },
    raw,
    readingSessions: {
      ...generatedGroups.readingSessions,
      create: (body: Record<string, unknown>) =>
        request("POST", "/v1/reading-sessions", { body }),
      list: (query?: ApiParams) =>
        request("GET", "/v1/reading-sessions", { query }),
    },
  };
};

const createQuranReflectFacade = (
  fetcher: QuranFetcher,
): QuranReflectFacade => {
  const raw = createRawClient(fetcher, operationCatalog.quranReflect.v1);
  const generatedGroups = createGeneratedGroups(
    operationCatalog.quranReflect.v1,
    raw,
  );

  return {
    comments: generatedGroups.comments ?? {},
    posts: createQuranReflectPostsFacade(
      generatedGroups.posts ?? {},
      createUserServiceRequest(fetcher, "quranReflect"),
    ),
    raw,
    rooms: generatedGroups.rooms ?? {},
    tags: generatedGroups.tags ?? {},
    users: generatedGroups.users ?? {},
  };
};

const createOAuth2Facade = (
  fetcher: QuranFetcher,
  mode: RuntimeMode,
  config: RuntimeClientConfig,
) => {
  const raw = createRawClient(fetcher, operationCatalog.oauth2.v1);

  const exchangeToken = async (
    body: URLSearchParams,
    options: {
      allowPublicExchange?: boolean;
      requiresRefreshToken?: boolean;
    } = {},
  ) => {
    if (mode === "public") {
      if ((config as PublicClientConfig).clientType !== "public") {
        throw new Error(
          "This OAuth2 exchange requires a backend because your client is confidential.",
        );
      }

      if (!options.allowPublicExchange) {
        throw new Error(
          "This OAuth2 flow requires @quranjs/api/server or a backend.",
        );
      }

      body.set("client_id", config.clientId);
    }

    if (options.requiresRefreshToken && !body.get("refresh_token")) {
      throw new Error("Missing refresh token.");
    }

    const requestRefreshToken = body.get("refresh_token") ?? undefined;
    const storedSession = options.requiresRefreshToken
      ? await fetcher.getUserSession()
      : null;
    const currentSession =
      options.requiresRefreshToken && requestRefreshToken
        ? { ...(storedSession ?? {}), refreshToken: requestRefreshToken }
        : storedSession;

    const token = await fetcher.request<TokenResponse>(
      "oauth2",
      "/oauth2/token",
      undefined,
      {
        auth: "none",
        basicAuth:
          mode === "server" && "clientSecret" in config
            ? {
                password: config.clientSecret,
                username: config.clientId,
              }
            : undefined,
        body,
        contentType: "application/x-www-form-urlencoded",
        method: "POST",
      },
    );

    await fetcher.setUserSession(toUserSession(token, currentSession));
    return token;
  };

  return {
    authorizeUrl: (query: Record<string, string | number | undefined>) =>
      fetcher.buildServiceUrl("oauth2", "/oauth2/auth", query as ApiParams),
    exchangeCode: (params: {
      code: string;
      codeVerifier?: string;
      redirectUri: string;
      scope?: string;
    }) => {
      const body = new URLSearchParams({
        code: params.code,
        grant_type: "authorization_code",
        redirect_uri: params.redirectUri,
      });

      if (params.codeVerifier) {
        body.set("code_verifier", params.codeVerifier);
      }

      if (params.scope) {
        body.set("scope", params.scope);
      }

      return exchangeToken(body, { allowPublicExchange: true });
    },
    getUserInfo: (accessToken?: string) =>
      fetcher.request("oauth2", "/userinfo", undefined, {
        accessToken,
        auth: "user",
        method: "GET",
      }),
    introspect: (token: string) => {
      if (mode !== "server" || !("clientSecret" in config)) {
        throw new Error(
          "Token introspection requires @quranjs/api/server or a backend.",
        );
      }

      return fetcher.request("oauth2", "/oauth2/introspect", undefined, {
        auth: "none",
        basicAuth: {
          password: config.clientSecret,
          username: config.clientId,
        },
        body: new URLSearchParams({ token }),
        contentType: "application/x-www-form-urlencoded",
        method: "POST",
      });
    },
    logout: (query?: ApiParams) =>
      fetcher.request("oauth2", "/oauth2/sessions/logout", query, {
        auth: "none",
        method: "GET",
      }),
    raw,
    refresh: async (refreshToken?: string) => {
      const currentSession = await fetcher.getUserSession();
      const body = new URLSearchParams({
        grant_type: "refresh_token",
        refresh_token: refreshToken ?? currentSession?.refreshToken ?? "",
      });

      return exchangeToken(body, {
        allowPublicExchange: true,
        requiresRefreshToken: true,
      });
    },
  };
};

const createContentFacade = (
  answers: QuranAnswers,
  chapters: QuranChapters,
  verses: QuranVerses,
  juzs: QuranJuzs,
  audio: QuranAudio,
  hadithReferences: QuranHadithReferences,
  resources: QuranResources,
  raw: Record<string, RawOperation>,
) => {
  return {
    answers: {
      byAyah: (key: VerseKey, query?: ApiParams) =>
        answers.findByAyah(key, query),
      countWithinRange: (from: VerseKey, to: VerseKey, query?: ApiParams) =>
        answers.countWithinRange(from, to, query),
      get: (questionId: string | number) =>
        answers.findByQuestionId(questionId),
    },
    audio: {
      chapterRecitation: {
        get: (reciterId: string, chapterId: ChapterId, query?: ApiParams) =>
          audio.findChapterRecitationById(reciterId, chapterId, query),
        list: (reciterId: string, query?: ApiParams) =>
          audio.findAllChapterRecitations(reciterId, query),
      },
      verseRecitation: {
        byChapter: (
          chapterId: ChapterId,
          recitationId: string,
          query?: ApiParams,
        ) =>
          audio.findVerseRecitationsByChapter(chapterId, recitationId, query),
        byKey: (verseKey: VerseKey, recitationId: string, query?: ApiParams) =>
          audio.findVerseRecitationsByKey(verseKey, recitationId, query),
      },
    },
    chapters: {
      get: (id: ChapterId, query?: ApiParams) => chapters.findById(id, query),
      getInfo: (id: ChapterId, query?: ApiParams) =>
        chapters.findInfoById(id, query),
      getInfoResponse: (id: ChapterId, query?: ApiParams) =>
        chapters.findInfoResponseById(id, query),
      list: (query?: ApiParams) => chapters.findAll(query),
    },
    hadithReferences: {
      byAyah: (key: VerseKey, query?: ApiParams) =>
        hadithReferences.findByAyah(key, query),
      countWithinRange: (from: VerseKey, to: VerseKey, query?: ApiParams) =>
        hadithReferences.countWithinRange(from, to, query),
      hadithsByAyah: (key: VerseKey, query?: ApiParams) =>
        hadithReferences.findHadithsByAyah(key, query),
    },
    juzs: {
      list: () => juzs.findAll(),
    },
    raw,
    resources: {
      findSnapshot: (
        resourceGroup: ContentSyncResourceGroup,
        id: string | number,
        query?: ApiParams,
      ) => resources.findSnapshot(resourceGroup, id, query),
      chapterInfos: {
        list: (query?: ApiParams) => resources.findAllChapterInfos(query),
      },
      chapterReciters: {
        list: (query?: ApiParams) => resources.findAllChapterReciters(query),
      },
      languages: {
        list: (query?: ApiParams) => resources.findAllLanguages(query),
      },
      recitationStyles: {
        list: (query?: ApiParams) => resources.findAllRecitationStyles(query),
      },
      recitations: {
        getInfo: (id: string, query?: ApiParams) =>
          resources.findRecitationInfo(id, query),
        list: (query?: ApiParams) => resources.findAllRecitations(query),
      },
      tafsirs: {
        getInfo: (id: string, query?: ApiParams) =>
          resources.findTafsirInfo(id, query),
        list: (query?: ApiParams) => resources.findAllTafsirs(query),
      },
      translations: {
        getInfo: (id: string, query?: ApiParams) =>
          resources.findTranslationInfo(id, query),
        list: (query?: ApiParams) => resources.findAllTranslations(query),
      },
      verseMedia: {
        list: (query?: ApiParams) => resources.findVerseMedia(query),
      },
      sync: (query?: ContentSyncOptions) => resources.sync(query),
    },
    verses: {
      byChapter: (id: ChapterId, query?: ApiParams) =>
        verses.findByChapter(id, query),
      byHizb: (id: HizbNumber, query?: ApiParams) =>
        verses.findByHizb(id, query),
      byJuz: (id: JuzNumber, query?: ApiParams) => verses.findByJuz(id, query),
      byKey: (key: VerseKey, query?: ApiParams) => verses.findByKey(key, query),
      byPage: (page: PageNumber, query?: ApiParams) =>
        verses.findByPage(page, query),
      byRange: (from: VerseKey, to: VerseKey, query?: ApiParams) =>
        verses.findByRange(from, to, query),
      byRub: (id: RubNumber, query?: ApiParams) => verses.findByRub(id, query),
      random: (query?: ApiParams) => verses.findRandom(query),
    },
  };
};

export const createRuntimeClient = (
  mode: RuntimeMode,
  config: RuntimeClientConfig,
) => {
  const fetcher = new QuranFetcher(mode, config);
  fetcher.getFetch();
  const answers = new QuranAnswers(fetcher);
  const chapters = new QuranChapters(fetcher);
  const verses = new QuranVerses(fetcher);
  const juzs = new QuranJuzs(fetcher);
  const audio = new QuranAudio(fetcher);
  const hadithReferences = new QuranHadithReferences(fetcher);
  const resources = new QuranResources(fetcher);
  const searchClient = new QuranSearch(fetcher);

  const raw = {
    analytics: {
      v1: createRawClient(fetcher, operationCatalog.analytics.v1),
    },
    auth: {
      v1: createRawClient(fetcher, operationCatalog.auth.v1),
    },
    content: {
      v4: createRawClient(fetcher, operationCatalog.content.v4),
    },
    oauth2: {
      v1: createRawClient(fetcher, operationCatalog.oauth2.v1),
    },
    quranReflect: {
      v1: createRawClient(fetcher, operationCatalog.quranReflect.v1),
    },
    search: {
      v1: createRawClient(fetcher, operationCatalog.search.v1),
    },
  };

  const contentV4 = createContentFacade(
    answers,
    chapters,
    verses,
    juzs,
    audio,
    hadithReferences,
    resources,
    raw.content.v4,
  );
  const analyticsV1 = createAnalyticsFacade(raw.analytics.v1);
  const authV1 = createAuthFacade(fetcher);
  const quranReflectV1 = createQuranReflectFacade(fetcher);
  const oauth2V1 = createOAuth2Facade(fetcher, mode, config);

  const query = ({
    query: searchQuery,
    ...options
  }: {
    query: string;
  } & Omit<SearchParams, "query">) => searchClient.search(searchQuery, options);

  const search = Object.assign(searchClient, {
    query,
    raw: raw.search.v1,
    v1: {
      query,
      raw: raw.search.v1,
    },
  });

  return {
    analytics: {
      ...analyticsV1,
      v1: analyticsV1,
    },
    answers,
    audio,
    auth: {
      ...authV1,
      v1: authV1,
    },
    chapters,
    clearCachedTokens: () => fetcher.clearCachedTokens(),
    content: {
      ...contentV4,
      v4: contentV4,
    },
    getUserSession: () => fetcher.getUserSession(),
    hadithReferences,
    juzs,
    oauth2: {
      ...oauth2V1,
      v1: oauth2V1,
    },
    quranReflect: {
      ...quranReflectV1,
      v1: quranReflectV1,
    },
    raw,
    resources,
    search,
    verses,
  };
};
